import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'

import { useTheme, type ResolvedTheme } from '../../contexts/ThemeContext'
import { formatAxisValue, formatSessionDate } from '../../lib/format'
import { paintQuadrantField, type FieldLean } from '../field/quadrantField'

/**
 * The state map: the field, and the reading ON it (Sprint 1.9.9 D-M4, R42).
 *
 * ── WHO DRAWS WHAT ──────────────────────────────────────────────────────────
 *
 * The canvas is the static field (`components/field/quadrantField.ts`), and it
 * is repainted only when its LEAN or the THEME changes — the dot is not one of
 * its inputs (test #27). Everything that places a reading is DOM: the dot, the
 * trail and the "your usual" ring are elements whose custom properties carry
 * the payload's numbers exactly as sent (`--act`, `--bal`, `--lo`, `--hi`,
 * `--dz`), and `mirror.css` positions them with `calc()`. There is no
 * arithmetic on a value in this file or anywhere in the client.
 *
 * The figure is `aria-hidden`: the word and the line beside it carry the
 * reading in words, and a picture described in client words would be a second,
 * client-authored account of the same reading.
 */

export interface MapPoint {
  act: number
  bal: number
}

export interface SelectableMapPoint extends MapPoint {
  id: string
  date: string
}

/** A number as the payload sent it, for a custom property: the string JavaScript writes, and no other. */
const raw = (n: number): string => String(n)

type Vars = CSSProperties & Record<`--${string}`, string>

export function QuadrantMap({
  lean,
  clip,
  dz,
  dot,
  dotToken,
  trail,
  corners,
  usualLabel,
  lock,
  onPainted,
  dotLabel = null,
  selectablePoints,
  selectedId,
  onSelect,
  testid = 'home',
}: {
  /** The field's lean: the reading's quadrant token, or null for an even field. */
  lean: FieldLean
  /** `[lo, hi]`, the axes' clip; with none, nothing can be placed. */
  clip: readonly [number, number] | null
  /** The ring's radius, `deadzone_z`. */
  dz: number | null
  dot: MapPoint | null
  /**
   * The reading's quadrant token, for the dot's colour class — one of the four
   * `mirror.css` names, which `Home.tsx` has already checked it is. A token
   * with no class gets no colour, never a guessed one.
   */
  dotToken: string | null
  trail: readonly MapPoint[]
  /** The corners' words, by position. */
  corners: { tl: string; tr: string; bl: string; br: string } | null
  usualLabel: string | null
  /** The learning lock's pill and caption (1.9.9 P3, D21). */
  lock: { pill: ReactNode; caption: string } | null
  /** Called with the theme each successful paint was for. */
  onPainted: (theme: ResolvedTheme) => void
  /** Interface copy beside the dot ("today"), placed by the dot's own values; none by default. */
  dotLabel?: string | null
  /** State's accessible controls sit outside the decorative field figure. */
  selectablePoints?: readonly SelectableMapPoint[]
  selectedId?: string
  onSelect?: (id: string) => void
  /**
   * The screen's test-id prefix: `home` (`home-map`, `home-map-dot`) or, where
   * `/state` reuses this map (1.9.9 D38), `state` or `state-patterns`.
   */
  testid?: string
}) {
  const { resolved } = useTheme()
  const canvas = useRef<HTMLCanvasElement | null>(null)
  const plot = useRef<HTMLDivElement | null>(null)
  /**
   * "Your usual" and "today" are placed by value with no knowledge of each
   * other, and near the usual ring they overlap. Measured after layout: when
   * the two chips intersect, the usual label moves to the other side of its
   * ring. A measurement, not arithmetic on a value.
   */
  const [usualBelow, setUsualBelow] = useState(false)
  // The usual label renders only on an unlocked map, so a lock change is a re-measure.
  const unlocked = lock === null
  useLayoutEffect(() => {
    const host = plot.current
    if (host === null) return
    const usual = host.querySelector<HTMLElement>('.qmap__usual')
    const today = host.querySelector<HTMLElement>('.qmap__dot-label')
    if (usual === null || today === null) { setUsualBelow(false); return }
    const measure = () => {
      usual.classList.remove('qmap__usual--below')
      const a = usual.getBoundingClientRect(), b = today.getBoundingClientRect()
      const hit = a.left < b.right + 4 && a.right > b.left - 4 && a.top < b.bottom + 4 && a.bottom > b.top - 4
      // The class was taken off to measure the label in its own place; put it back to
      // match, because a state that did not change does not re-render.
      usual.classList.toggle('qmap__usual--below', hit)
      setUsualBelow(hit)
    }
    measure()
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    ro?.observe(host)
    // The chips too: a web font arriving resizes them without resizing the plot.
    ro?.observe(usual)
    ro?.observe(today)
    return () => ro?.disconnect()
  }, [dot, dz, clip, usualLabel, dotLabel, unlocked])

  useEffect(() => {
    if (canvas.current === null) return
    if (paintQuadrantField(canvas.current, lean, resolved)) onPainted(resolved)
    // `onPainted` is the parent's setter; the field depends on the lean and the
    // theme and on nothing else — above all not on the dot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lean, resolved])

  const frame: Vars | undefined = clip === null
    ? undefined
    : { '--lo': raw(clip[0]), '--hi': raw(clip[1]), ...(dz === null ? {} : { '--dz': raw(dz) }) }

  return (
    <div className={lock === null ? 'qmap' : 'qmap qmap--locked'} data-testid={`${testid}-map`}>
      <div className="qmap__stage">
        <figure className="qmap__figure" style={frame} aria-hidden="true">
          <canvas className="qmap__canvas" ref={canvas} />
          <span className="qmap__axis qmap__axis--v" />
          <span className="qmap__axis qmap__axis--h" />
          {/* The plotting box: everything placed by a value, inset from the edge. */}
          <div className="qmap__plot" ref={plot}>
            {clip !== null && dz !== null && <span className="qmap__ring" data-testid={`${testid}-map-ring`} />}
            {clip !== null && dz !== null && usualLabel !== null && lock === null && (
              <span className={usualBelow ? 'qmap__usual qmap__usual--below' : 'qmap__usual'}>{usualLabel}</span>
            )}
            {selectablePoints === undefined && clip !== null && lock === null && trail.map((p, i) => (
              <span
                key={i}
                className="qmap__pt qmap__trail"
                style={{ '--act': raw(p.act), '--bal': raw(p.bal), '--i': String(i), '--n': String(trail.length) } as Vars}
              />
            ))}
            {selectablePoints === undefined && clip !== null && lock === null && dot !== null && (
              <span
                className={dotToken === null ? 'qmap__pt qmap__dot' : `qmap__pt qmap__dot mirror-q--${dotToken}`}
                data-testid={`${testid}-map-dot`}
                style={{ '--act': raw(dot.act), '--bal': raw(dot.bal) } as Vars}
              />
            )}
            {selectablePoints === undefined && clip !== null && lock === null && dot !== null && dotLabel !== null && (
              <span className="qmap__pt qmap__dot-label" style={{ '--act': raw(dot.act), '--bal': raw(dot.bal) } as Vars}>
                {dotLabel}
              </span>
            )}
          </div>
          {corners !== null && (
            <>
              <span className="qmap__corner qmap__corner--tl">{corners.tl}</span>
              <span className="qmap__corner qmap__corner--tr">{corners.tr}</span>
              <span className="qmap__corner qmap__corner--bl">{corners.bl}</span>
              <span className="qmap__corner qmap__corner--br">{corners.br}</span>
            </>
          )}
        </figure>
        {selectablePoints !== undefined && clip !== null && lock === null && onSelect !== undefined && (
          <div className="qmap__controls">
            {selectablePoints.map((point) => (
              <button key={point.id} type="button" className="qmap__select"
                style={{ '--act': raw(point.act), '--bal': raw(point.bal), '--lo': raw(clip[0]), '--hi': raw(clip[1]) } as Vars}
                aria-pressed={point.id === selectedId}
                aria-label={`${formatSessionDate(point.date)} · activation ${formatAxisValue(point.act)}, balance ${formatAxisValue(point.bal)}`}
                onClick={() => onSelect(point.id)}>
                <span className="qmap__select-tip">{formatSessionDate(point.date)} · {formatAxisValue(point.act)} / {formatAxisValue(point.bal)}</span>
                {point.id === selectedId && <span className="qmap__select-date" aria-hidden="true">{formatSessionDate(point.date)}</span>}
              </button>
            ))}
          </div>
        )}
        {/* Outside the hidden figure, so the countdown is read as well as seen. */}
        {lock !== null && <div className="qmap__lock">{lock.pill}</div>}
      </div>
      {lock !== null && <p className="qmap__caption">{lock.caption}</p>}
    </div>
  )
}
